import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { jwtDecode } from "jwt-decode";
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../models/user.model';

@Injectable()
export class UserService {
  apiURL: string = environment.apiURL;

  constructor(private http: HttpClient) {}

  /**
   * Check session expiration
   */
  isSessionValid(): boolean {
    // checking session expiration
    const token = localStorage.getItem('AccessToken') || undefined;

    if (token !== null) {
      const decodedJWT = jwtDecode(token);
      const expirationEpoh = decodedJWT.exp;
      const currentEpoh = new Date().getTime() / 1000;

      return currentEpoh > expirationEpoh ? false : true;
    }
    return false;
  }

  /**
   * Is user signed in?
   */
  isSignedIn(): boolean {
    return localStorage.getItem('AccessToken') !== null && this.isSessionValid();
  }

  /**
   * Get logged in user
   */
  getMe(): User | null {
    const userData = localStorage.getItem('User');
    return userData ? JSON.parse(userData) : null;
  }

  /**
   * Get roles from localStorage
   */
  getRoleList(): Array<string> {
    const rolesData = localStorage.getItem('Roles');
    return rolesData ? JSON.parse(rolesData) : null;
  }

  /**
   * Get users
   */
  getUsers(skip?: number, limit?: number, name?: string, roles?: Array<string>, isActive?: boolean): Observable<any> {
    let params = new HttpParams()
      .set('skip', skip)
      .set('limit', limit);

    if (name) params = params.append('name', name);
    if (isActive) params = params.append('isActive', isActive);

    if (roles && roles.length > 0) {
      roles.forEach(role => {
        params = params.append('role', role);
      })
    }

    return this.http.get(`${this.apiURL}/users`, { params });
  }

  /**
   * Get logged in user
   */
  getUserProfile(): Observable<any> {
    return this.http.get(`${this.apiURL}/profile`);
  }

  /**
   * Get user details
   */
  getUserDetails(userId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/users/${userId}`);
  }

  /**
   * Get logged in User role
   */
  getRole(): string | null {
    const user = this.getMe();
    return user.role ?? null;
  }

  /**
   * Get roles
   */
  getRoles(): Observable<any> {
    return this.http.get(`${this.apiURL}/enums?enumType=roles`);
  }

  /**
   * Save AccessToken to localStorage
   */
  saveAccessToken(token: string): void {
    if (token) {
      localStorage.setItem('AccessToken', token);
    }
  }

  /**
   * Get JWT access token
   */
  getAccessToken(): string {
    return this.isSignedIn() ? localStorage.getItem('AccessToken') : '';
  }

  /**
   * Save User to localStorage
   */
  saveUser(data: any): void {
    if (data) {
      localStorage.setItem('User', JSON.stringify(data));
    }
  }

  /**
   * Sign in User
   */
  signIn(data: { email: string; password: string }): Observable<any> {
    return this.http.post(`${this.apiURL}/signin`, data);
  }

  /**
   * Change password
   */
  changePassword(data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/change-password`, data);
  }

  /**
   * Request change for forgotten password
   */
  forgotPassword(data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/forgot-password`, data);
  }

  /**
   * Edit user profile
   */
  editUser(userId: string, data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/users/${userId}`, data);
  }

  /**
   * Edit my profile
   */
  editProfile(data: any): Observable<any> {
    return this.http.patch(`${this.apiURL}/profile`, data);
  }

  /**
   * Create user profile
   */
  createUser(data: any): Observable<any> {
    return this.http.post(`${this.apiURL}/users`, data);
  }

  /**
   * Reset forgotten password
   */
  resetPassword(password: any, resetToken: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/reset-password/${resetToken}`, { password });
  }

  /**
   * Change user status
   */
  changeUserStatus(userId: string): Observable<any> {
    return this.http.patch(`${this.apiURL}/users/${userId}/activate`, {});
  }

  /**
   * Get user permissions
   */
  getUserPermission(userId: string): Observable<any> {
    return this.http.get(`${this.apiURL}/users/${userId}/permissions`);
  }

  /**
   * Edit user permissions
   */
  editUserPermission(userId: string, data): Observable<any> {
    return this.http.patch(`${this.apiURL}/users/${userId}/permissions`, data);
  }
}
